import { action, observable } from "mobx";

import { loadSettings, saveSettings } from "./savedSettings";

const KEY = "bandstand.display";

/** The text sizes to choose from, in pixels. */
export const TEXT_SIZES = { smallest: 14, largest: 40 };

/** The space between sections to choose from, in lines of text. */
export const SECTION_SPACING = { smallest: 0, largest: 3 };

/** The space between lines to choose from, as a multiple of the text size. */
export const LINE_SPACING = { smallest: 1.1, largest: 2 };

/** The chord sizes to choose from, as a multiple of their standard size, a bit smaller than the lyrics. */
export const CHORD_SIZES = { smallest: 0.8, largest: 1.6 };

/** The font of the lyrics. */
export type SheetFont = "sans" | "serif";

const DEFAULTS = {
    textSize: 20,
    lyricsOnly: false,
    splitSections: false,
    sectionSpacing: 1.2,
    lineSpacing: 1.45,
    chordSize: 1,
    font: "sans" as SheetFont,
};

/**
 * How chord sheets are shown on this device, for every song: text size, whether to hide the chords, whether sections
 * may be split over two columns, the spacing, the size of the chords and the font. These depend on who holds the
 * device, so they're kept on it, never in a chord sheet or setlist.
 */
export class DisplayStore {
    @observable accessor textSize = DEFAULTS.textSize;

    @observable accessor lyricsOnly = DEFAULTS.lyricsOnly;

    /** Whether a section may be split over two columns to fill them, instead of moving to the next one as a whole. */
    @observable accessor splitSections = DEFAULTS.splitSections;

    /** The space between sections, in lines of text. */
    @observable accessor sectionSpacing = DEFAULTS.sectionSpacing;

    /** The space between lines, as a multiple of the text size. */
    @observable accessor lineSpacing = DEFAULTS.lineSpacing;

    /** How big chords are, as a multiple of their standard size. */
    @observable accessor chordSize = DEFAULTS.chordSize;

    @observable accessor font = DEFAULTS.font;

    constructor() {
        const saved = loadSettings(KEY, DEFAULTS);

        Object.assign(this, { ...saved, font: saved.font === "serif" ? "serif" : "sans" });
    }

    @action
    changeTextSize(by: number): void {
        this.setTextSize(this.textSize + by);
    }

    @action
    setTextSize(size: number): void {
        this.textSize = within(TEXT_SIZES, size);
        this.save();
    }

    @action
    setLyricsOnly(lyricsOnly: boolean): void {
        this.lyricsOnly = lyricsOnly;
        this.save();
    }

    @action
    setSplitSections(splitSections: boolean): void {
        this.splitSections = splitSections;
        this.save();
    }

    @action
    setSectionSpacing(spacing: number): void {
        this.sectionSpacing = within(SECTION_SPACING, spacing);
        this.save();
    }

    @action
    setLineSpacing(spacing: number): void {
        this.lineSpacing = within(LINE_SPACING, spacing);
        this.save();
    }

    @action
    setChordSize(size: number): void {
        this.chordSize = within(CHORD_SIZES, size);
        this.save();
    }

    @action
    setFont(font: SheetFont): void {
        this.font = font;
        this.save();
    }

    /**
     * Takes settings from elsewhere, like the share server. Text size stays, as it depends on this screen. Values
     * out of range are brought within it.
     */
    @action
    apply(saved: Omit<typeof DEFAULTS, "textSize" | "font"> & { font: string }): void {
        this.lyricsOnly = saved.lyricsOnly;
        this.splitSections = saved.splitSections;
        this.sectionSpacing = within(SECTION_SPACING, saved.sectionSpacing);
        this.lineSpacing = within(LINE_SPACING, saved.lineSpacing);
        this.chordSize = within(CHORD_SIZES, saved.chordSize);
        this.font = saved.font === "serif" ? "serif" : "sans";
        this.save();
    }

    /** Back to how the sheet looks at first. Lyrics only stays: it's about who's reading, not how it looks. */
    @action
    reset(): void {
        Object.assign(this, { ...DEFAULTS, lyricsOnly: this.lyricsOnly });
        this.save();
    }

    private save(): void {
        const { textSize, lyricsOnly, splitSections, sectionSpacing, lineSpacing, chordSize, font } = this;

        saveSettings(KEY, { textSize, lyricsOnly, splitSections, sectionSpacing, lineSpacing, chordSize, font });
    }
}

function within(range: { smallest: number; largest: number }, value: number): number {
    return Math.min(range.largest, Math.max(range.smallest, value));
}
